import { logger } from './logger';

const R2_BASE = 'https://pub-cc0bc47961a44ee2bc8914a781dded90.r2.dev';

const SOUNDTRACK_URL = `${R2_BASE}/sound/Prehistoric_Fury.mp3`;
const MUTE_KEY = 'dk_muted';

class AudioManager {
  private bgm: HTMLAudioElement | null = null;
  private bgmReady = false;
  private userInteracted = false;
  private muted = false;
  private muteBtn: HTMLButtonElement | null = null;

  constructor() {
    // Restore mute preference, or default to muted on mobile
    try {
      const stored = localStorage.getItem(MUTE_KEY);
      if (stored !== null) {
        this.muted = stored === '1';
      } else {
        // No preference saved — mute by default on touch/mobile devices
        this.muted = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      }
    } catch {
      // localStorage may be unavailable
    }
  }

  /**
   * Preload the soundtrack. Call early so it's buffered by the time
   * the user starts a fight.
   */
  preload(): void {
    if (this.bgm) return;

    try {
      this.bgm = new Audio(SOUNDTRACK_URL);
      this.bgm.loop = true;
      this.bgm.volume = 0.4;
      this.bgm.preload = 'auto';

      this.bgm.addEventListener('canplaythrough', () => {
        this.bgmReady = true;
        logger.info('Soundtrack loaded');
        // If the user already interacted, start playing immediately
        if (this.userInteracted && !this.muted) {
          this.play();
        }
      });

      this.bgm.addEventListener('error', (e) => {
        logger.error('Soundtrack failed to load', e);
      });

      // Mobile browsers often ignore preload='auto' — explicitly kick the load
      this.bgm.load();
    } catch (err) {
      logger.error('Audio init failed', err);
    }
  }

  /**
   * Mark that the user has interacted (click/key/touch).
   * Browsers block autoplay until a user gesture — call this from
   * the first interaction handler.
   *
   * On mobile, play() MUST be called within the gesture callstack,
   * so we call it here directly rather than deferring to canplaythrough.
   */
  unlock(): void {
    if (this.userInteracted) return;
    this.userInteracted = true;

    if (!this.muted && this.bgm) {
      // Call play() directly in the gesture handler callstack.
      // On mobile this is required even if the audio isn't fully loaded yet —
      // the browser remembers this element was play()-ed from a gesture and
      // will allow it to play once data arrives.
      this.bgm.play().catch((err) => {
        logger.warn(`BGM play blocked on unlock: ${err}`);
      });
    }
  }

  /**
   * Start playing the background music. Only works after both
   * preload completed and user interacted.
   */
  private play(): void {
    if (!this.bgm || this.muted) return;

    this.bgm.play().catch((err) => {
      logger.warn(`BGM play blocked: ${err}`);
    });
  }

  /** Pause the background music (e.g., on tab blur). */
  pause(): void {
    this.bgm?.pause();
  }

  /** Resume after pause (e.g., on tab focus). */
  resume(): void {
    if (this.userInteracted && !this.muted && this.bgmReady) {
      this.play();
    }
  }

  /** Toggle mute. Returns the new muted state. */
  toggleMute(): boolean {
    this.muted = !this.muted;

    // Persist preference
    try {
      localStorage.setItem(MUTE_KEY, this.muted ? '1' : '0');
    } catch {
      // localStorage may be unavailable
    }

    if (this.bgm) {
      if (this.muted) {
        this.bgm.pause();
      } else if (this.userInteracted && this.bgmReady) {
        this.play();
      }
    }

    this.updateMuteButton();
    return this.muted;
  }

  /** Check whether audio is muted. */
  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Create the audio on/off toggle button (fixed HTML overlay).
   * Placed top-right, next to the LOG button.
   */
  initToggleButton(): void {
    if (this.muteBtn) return;

    const btn = document.createElement('button');
    btn.id = 'audio-toggle';
    btn.textContent = this.muted ? 'SND OFF' : 'SND ON';
    btn.style.cssText = [
      'position:fixed',
      'top:4px',
      'right:52px',
      'z-index:100000',
      'background:rgba(40,40,40,0.7)',
      `color:${this.muted ? '#f44' : '#0f0'}`,
      `border:1px solid ${this.muted ? '#f44' : '#0f0'}`,
      'border-radius:4px',
      'font:bold 10px/1 monospace',
      'padding:4px 8px',
      'cursor:pointer',
      'touch-action:manipulation',
      '-webkit-tap-highlight-color:transparent',
    ].join(';');

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Also unlock audio if this is the first interaction
      this.unlock();
      this.toggleMute();
    });

    document.body.appendChild(btn);
    this.muteBtn = btn;
  }

  private updateMuteButton(): void {
    if (!this.muteBtn) return;
    this.muteBtn.textContent = this.muted ? 'SND OFF' : 'SND ON';
    this.muteBtn.style.color = this.muted ? '#f44' : '#0f0';
    this.muteBtn.style.borderColor = this.muted ? '#f44' : '#0f0';
  }
}

/** Global singleton — import from anywhere. */
export const audio = new AudioManager();
